import { useRouter } from "next/router";
import { GetServerSideProps } from "next/types";
import { useEffect, useMemo } from "react";
import { SEO } from "../../components/seo/seo";
import { SellerModule } from "../../modules/seller";
import {
  mapSellerProfileResultToSellerProfile,
  SellerProfile,
} from "../../stores/seller-store/seller-store.model";
import { isCrediMart, isCrediStore, normalizeUrl } from "../../utils/helper";

interface SellerPageProps {
  sellerData: SellerProfile;
  isReseller: boolean;
}

const SellerPage = ({ sellerData, isReseller }: SellerPageProps) => {
  const router = useRouter();

  const title = useMemo(() => {
    const { seller } = router.query;
    const isOnResellerPath = seller === process.env.NEXT_PUBLIC_RESELLER_PATH;
    if (isReseller || (isOnResellerPath && !sellerData)) {
      return "Jooalan - Mulai cuanmu sekarang";
    }
    return sellerData ? sellerData?.User?.name : "Toko tidak ditemukan";
  }, [isReseller, router.query, sellerData]);

  const description = useMemo(() => {
    if (isReseller) {
      return "Gak perlu antre panjang buat belanja kebutuhan usaha. Di CrediMart, Anda bisa belanja grosir secara online, praktis, bayarnya bisa belakangan.";
    }
    return (
      sellerData && `${sellerData?.address}, Telp. ${sellerData?.User.phone}`
    );
  }, [isReseller, sellerData]);

  useEffect(() => {
    // setup dynamic manifest for any seller store except reseller
    if (
      !sellerData?.isReseller &&
      sellerData?.User?.username !== process.env.NEXT_PUBLIC_RESELLER_PATH
    ) {
      const dynamicManifest = {
        name: `${sellerData?.User?.name} | ${process.env.NEXT_PUBLIC_BRAND_NAME}`,
        short_name: sellerData?.User?.name,
        description: process.env.NEXT_PUBLIC_BRAND_TAGLINE,
        start_url: `${window.location.origin}/${sellerData?.User?.username}`,
        display: "standalone",
        orientation: "portrait",
        background_color: "#fff",
        theme_color: "#fff",
        icons: [
          {
            src: `${window.location.origin}/img/${process.env.NEXT_PUBLIC_APP_NAME}/icons/icon-72x72.png`,
            sizes: "72x72",
            type: "image/png",
            purpose: "maskable",
          },
          {
            src: `${window.location.origin}/img/${process.env.NEXT_PUBLIC_APP_NAME}/icons/icon-96x96.png`,
            sizes: "96x96",
            type: "image/png",
            purpose: "maskable",
          },
          {
            src: `${window.location.origin}/img/${process.env.NEXT_PUBLIC_APP_NAME}/icons/icon-128x128.png`,
            sizes: "128x128",
            type: "image/png",
            purpose: "maskable",
          },
          {
            src: `${window.location.origin}/img/${process.env.NEXT_PUBLIC_APP_NAME}/icons/icon-144x144.png`,
            sizes: "144x144",
            type: "image/png",
          },
          {
            src: `${window.location.origin}/img/${process.env.NEXT_PUBLIC_APP_NAME}/icons/icon-152x152.png`,
            sizes: "152x152",
            type: "image/png",
          },
          {
            src: `${window.location.origin}/img/${process.env.NEXT_PUBLIC_APP_NAME}/icons/icon-192x192.png`,
            sizes: "192x192",
            type: "image/png",
          },
          {
            src: `${window.location.origin}/img/${process.env.NEXT_PUBLIC_APP_NAME}/icons/icon-384x384.png`,
            sizes: "384x384",
            type: "image/png",
          },
          {
            src: `${window.location.origin}/img/${process.env.NEXT_PUBLIC_APP_NAME}/icons/icon-512x512.png`,
            sizes: "512x512",
            type: "image/png",
          },
        ],
      };
      const manifestString = JSON.stringify(dynamicManifest);
      const blob = new Blob([manifestString], { type: "application/json" });
      const manifestURL = URL.createObjectURL(blob);
      document.querySelector("#manifest").setAttribute("href", manifestURL);
    }
  }, [sellerData]);

  return (
    <>
      <SEO
        title={title}
        description={description}
        path={normalizeUrl(router.asPath)}
      />
      <SellerModule sellerData={sellerData} isReseller={isReseller} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const { seller: sellerUsername } = context.params;

    // fetch(`${process.env.NEXT_PUBLIC_API_URL}/guest/seller-count?username=${sellerUsername}`);

    const sellerResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/guest/seller-profile?username=${sellerUsername}`);

    const sellerJson = await sellerResponse.json();
    const sellerData = mapSellerProfileResultToSellerProfile(sellerJson);

    const isReseller = sellerData?.isReseller && sellerData?.User?.username === process.env.NEXT_PUBLIC_RESELLER_PATH;
    const isOnResellerPath = sellerUsername === process.env.NEXT_PUBLIC_RESELLER_PATH;

    // restrict access by seller type
    if (
      (!isOnResellerPath && !sellerData) ||
      (isCrediMart() && !sellerData?.sellerJopan) ||
      (isCrediStore() && sellerData?.sellerJopan)
    ) {
      return {
        redirect: {
          permanent: false,
          destination: "/",
        },
      };
    }

    return {
      props: {
        sellerData,
        isReseller,
        isOnResellerPath,
      },
    };
  } catch (err) {
    return { props: {} };
  }
};

export default SellerPage;
